import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MeetingCalendarPicker } from "@/components/agent/details/MeetingCalendarPicker";
import { Switch } from "@/components/ui/switch";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { useRole } from "@/hooks/auth/useRole";
import {
  useCancelGenieMeeting,
  useConnectGenieCalendar,
  useDisconnectGenieCalendar,
  useGenieCalendarEvents,
  useGenieCalendars,
  useSendGenieToMeeting,
  useUpdateGenieCalendar,
} from "@/hooks/agent/useGenieMeetings";
import {
  CALENDAR_PROVIDER_LABELS,
  EVENT_SELECTION_OPTIONS,
  formatMeetingMoment,
  isCancellableMeeting,
  PROVIDER_LABELS,
  STATE_LABELS,
} from "@/lib/agent/genieMeetings";
import { Agent } from "@/types/agent/agent";
import {
  CalendarProvider,
  GenieCalendarConnection,
  UpcomingCalendarEvent,
  UpdateGenieCalendarPayload,
} from "@/types/agent/genie-meeting";

interface MeetingCalendarSectionProps {
  agent: Agent;
  brand: string;
}

interface CalendarPanelProps extends MeetingCalendarSectionProps {
  connection: GenieCalendarConnection;
  /** All the genie's calendars, this one included. */
  connections: GenieCalendarConnection[];
  onConnect: (provider: CalendarProvider, connectionId?: string) => void;
  connectingProvider: CalendarProvider | null;
}

const FIELD_LABEL = "text-sm font-semibold text-gray-700 mb-2 block";
const SELECT_CLASS =
  "w-full min-h-[44px] px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm";
const MUTED = "#6B7280";
const DANGER = "#DC2626";
// The genie needs a moment to get there; anything sooner joins right away.
const MIN_SCHEDULE_LEAD_MS = 3 * 60_000;
const MIN_PARTICIPANT_OPTIONS = [2, 3, 4, 5, 10];
// Outlook stays admin-only until Microsoft verifies our publisher: before that, users outside our tenant can't consent.
const PUBLIC_CALENDAR_PROVIDERS: CalendarProvider[] = ["google"];
const ADMIN_CALENDAR_PROVIDERS: CalendarProvider[] = ["google", "microsoft"];

const eventTimeLine = (event: UpcomingCalendarEvent): string => {
  const start = formatMeetingMoment(event.start);
  if (!event.end) return start;
  return `${start} – ${new Date(event.end).toLocaleTimeString(undefined, { timeStyle: "short" })}`;
};

const calendarLabel = (connection: GenieCalendarConnection) =>
  connection.calendar_name ?? connection.email ?? CALENDAR_PROVIDER_LABELS[connection.provider];

export const MeetingCalendarSection = ({ agent, brand }: MeetingCalendarSectionProps) => {
  const { data, isLoading } = useGenieCalendars(agent.id);
  const connections = data?.connections ?? [];
  const canAdd = connections.length < (data?.maxConnections ?? 1);

  const { isInternalAdmin } = useRole();
  const connectableProviders = isInternalAdmin() ? ADMIN_CALENDAR_PROVIDERS : PUBLIC_CALENDAR_PROVIDERS;
  const connectMutation = useConnectGenieCalendar(agent.id);
  const connectingProvider = connectMutation.isPending ? connectMutation.variables?.provider ?? null : null;

  const softButtonStyle = { backgroundColor: `${brand}15`, color: brand };
  const providerNames = connectableProviders.includes("microsoft") ? "a Google or Outlook calendar" : "a Google Calendar";

  const handleConnect = async (provider: CalendarProvider, connectionId?: string) => {
    try {
      await connectMutation.mutateAsync({ provider, connectionId });
    } catch (err: any) {
      toast.error(err.message ?? "The calendar couldn't be connected");
    }
  };

  const connectButtons = (
    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
      {connectableProviders.map((provider) => (
        <button
          key={provider}
          type="button"
          onClick={() => handleConnect(provider)}
          disabled={connectMutation.isPending}
          className="min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          style={softButtonStyle}
        >
          {connectingProvider === provider && <Loader2 className="h-4 w-4 animate-spin" />}
          {`Connect ${CALENDAR_PROVIDER_LABELS[provider]}`}
        </button>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
        <span className="text-sm text-gray-600">Checking the calendar...</span>
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div
        className="rounded-xl border p-4 sm:p-5 space-y-4"
        style={{ borderColor: `${brand}25`, backgroundColor: `${brand}08` }}
      >
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${brand}15` }}
          >
            <GenieIcon name="calendar-check" size={24} color={brand} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">{`Let ${agent.name} join from your calendar`}</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {`Connect ${providerNames} and ${agent.name} joins its online meetings on its own, no links to paste. It only reads your calendars and events and never changes them.`}
            </p>
            <p className="text-xs text-gray-500 mt-1.5">
              {`Use Calendly, Cal.com or another booking tool? Connect the calendar it adds bookings to and ${agent.name} picks those meetings up too.`}
            </p>
          </div>
        </div>
        {connectButtons}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {connections.map((connection, index) => (
        <div key={connection.id} className={index > 0 ? "border-t border-gray-100 pt-5" : undefined}>
          <CalendarPanel
            agent={agent}
            brand={brand}
            connection={connection}
            connections={connections}
            onConnect={handleConnect}
            connectingProvider={connectingProvider}
          />
        </div>
      ))}

      {canAdd && (
        <div className="border-t border-gray-100 pt-5 space-y-3">
          <div>
            <p className="text-sm font-semibold text-gray-700">Add another calendar</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {`Keep work and personal meetings on different calendars? Connect ${providerNames} too. A meeting on more than one of them is joined once.`}
            </p>
          </div>
          {connectButtons}
        </div>
      )}
    </div>
  );
};

/** One connected calendar: its picked calendar, its rules and what's coming up on it. */
const CalendarPanel = ({ agent, brand, connection, connections, onConnect, connectingProvider }: CalendarPanelProps) => {
  const [changingCalendar, setChangingCalendar] = useState(false);
  const picking = !connection.invalid_at && (!connection.calendar_id || changingCalendar);
  const connected = !connection.invalid_at && !!connection.calendar_id;
  const { data: upcoming, isLoading: eventsLoading } = useGenieCalendarEvents(
    agent.id,
    connection.id,
    connected && !picking,
  );

  const updateMutation = useUpdateGenieCalendar(agent.id, connection.id);
  const disconnectMutation = useDisconnectGenieCalendar(agent.id, connection.id);
  const sendMutation = useSendGenieToMeeting(agent.id);
  const cancelMutation = useCancelGenieMeeting(agent.id);

  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const [togglingEventId, setTogglingEventId] = useState<string | null>(null);

  const softButtonStyle = { backgroundColor: `${brand}15`, color: brand };
  const hasOthers = connections.length > 1;

  const handleUpdate = async (payload: UpdateGenieCalendarPayload) => {
    try {
      await updateMutation.mutateAsync(payload);
    } catch (err: any) {
      toast.error(err.message ?? "The calendar settings couldn't be saved");
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      setConfirmingDisconnect(false);
      toast.success("Calendar disconnected");
    } catch (err: any) {
      toast.error(err.message ?? "The calendar couldn't be disconnected");
    }
  };

  const handleToggle = async (event: UpcomingCalendarEvent, going: boolean) => {
    setTogglingEventId(event.id);
    try {
      if (going) {
        const startsIn = new Date(event.start).getTime() - Date.now();
        await sendMutation.mutateAsync({
          meetingLink: event.meeting_link,
          title: event.title ?? undefined,
          joinTime: startsIn > MIN_SCHEDULE_LEAD_MS ? event.start : undefined,
          emailNotes: connection.email_notes,
          calendarEventId: event.id,
          calendarConnectionId: connection.id,
        });
        toast.success(`${agent.name} will join ${event.title ?? "this meeting"}`);
      } else if (event.meeting) {
        await cancelMutation.mutateAsync(event.meeting.id);
        toast.success(`${agent.name} will skip ${event.title ?? "this meeting"}`);
      }
    } catch (err: any) {
      toast.error(err.message ?? "That meeting couldn't be changed");
    } finally {
      setTogglingEventId(null);
    }
  };

  /** Where the genie's meeting for this event came from, when not this calendar. */
  const elsewhereNote = (event: UpcomingCalendarEvent): string | null => {
    if (!event.elsewhere || !event.meeting) return null;
    const source = connections.find((candidate) => candidate.id === event.meeting!.calendar_connection_id);
    return source
      ? `Also on ${calendarLabel(source)}: ${agent.name} joins it once, from there.`
      : `You already sent ${agent.name} to this meeting.`;
  };

  const disconnectConfirm = (
    <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
      <p className="text-sm text-gray-700">
        {agent.name} stops joining this calendar's meetings. Meetings it booked that haven't started are cancelled;
        ones you switched on yourself stay.
        {hasOthers && ` If another of its calendars has the same meeting, ${agent.name} joins it from there.`}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleDisconnect}
          disabled={disconnectMutation.isPending}
          className="min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          style={{ backgroundColor: `${DANGER}12`, color: DANGER }}
        >
          {disconnectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Disconnect
        </button>
        <button
          type="button"
          onClick={() => setConfirmingDisconnect(false)}
          className="min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors"
          style={softButtonStyle}
        >
          Keep it connected
        </button>
      </div>
    </div>
  );

  if (connection.invalid_at) {
    return (
      <div
        className="rounded-xl border p-4 sm:p-5 space-y-4"
        style={{ borderColor: `${brand}25`, backgroundColor: `${brand}08` }}
      >
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${brand}15` }}
          >
            <GenieIcon name="calendar-check" size={24} color={brand} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900">Connect your calendar again</p>
            <p className="text-xs text-gray-600 mt-0.5">
              {`${agent.name} can no longer see ${connection.email ?? "the calendar"}, so it won't join its meetings on its own. Connect it again to carry on.`}
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onConnect(connection.provider, connection.id)}
            disabled={!!connectingProvider}
            className="min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={softButtonStyle}
          >
            {connectingProvider === connection.provider && <Loader2 className="h-4 w-4 animate-spin" />}
            {`Reconnect ${CALENDAR_PROVIDER_LABELS[connection.provider]}`}
          </button>
          {hasOthers && !confirmingDisconnect && (
            <button
              type="button"
              onClick={() => setConfirmingDisconnect(true)}
              className="min-h-[44px] px-4 text-sm text-genie-primary hover:underline"
            >
              Disconnect
            </button>
          )}
        </div>
        {confirmingDisconnect && disconnectConfirm}
      </div>
    );
  }

  const selected = EVENT_SELECTION_OPTIONS.find((option) => option.value === connection.event_selection);
  const events = upcoming?.events ?? [];
  const fieldId = (name: string) => `calendar-${connection.id}-${name}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${brand}15` }}
          >
            <GenieIcon name="calendar-check" size={24} color={brand} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {connection.calendar_name ? `Joining from ${connection.calendar_name}` : "Calendar connected"}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {[connection.email, CALENDAR_PROVIDER_LABELS[connection.provider]].filter(Boolean).join(" · ")}
            </p>
          </div>
        </div>
        {!confirmingDisconnect && (
          <div className="flex items-center gap-4 self-start sm:self-auto">
            {connection.calendar_id && !changingCalendar && (
              <button
                type="button"
                onClick={() => setChangingCalendar(true)}
                className="min-h-[44px] text-sm text-genie-primary hover:underline"
              >
                Change calendar
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmingDisconnect(true)}
              className="min-h-[44px] text-sm text-genie-primary hover:underline"
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {confirmingDisconnect && disconnectConfirm}

      {picking ? (
        <MeetingCalendarPicker
          agent={agent}
          brand={brand}
          connectionId={connection.id}
          currentCalendarId={connection.calendar_id}
          onCancel={connection.calendar_id ? () => setChangingCalendar(false) : undefined}
          onPicked={() => setChangingCalendar(false)}
        />
      ) : (
        <>
          <div>
            <span className={FIELD_LABEL}>Which meetings to join</span>
            <div className="flex flex-wrap gap-2">
              {EVENT_SELECTION_OPTIONS.map((option) => {
                const active = connection.event_selection === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => !active && handleUpdate({ eventSelection: option.value })}
                    disabled={updateMutation.isPending}
                    aria-pressed={active}
                    className="min-h-[44px] px-4 rounded-xl text-sm font-medium border transition-colors disabled:opacity-60"
                    style={
                      active
                        ? { ...softButtonStyle, borderColor: `${brand}55` }
                        : { color: MUTED, borderColor: "#E5E7EB" }
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {selected && <p className="text-xs text-gray-500 mt-2">{selected.hint}</p>}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 sm:items-end">
            <div>
              <label htmlFor={fieldId("min-participants")} className={FIELD_LABEL}>
                Meeting size
              </label>
              <select
                id={fieldId("min-participants")}
                value={connection.min_participants ?? ""}
                onChange={(e) =>
                  handleUpdate({
                    minParticipants: e.target.value ? Number(e.target.value) : null,
                  })
                }
                disabled={updateMutation.isPending}
                className={SELECT_CLASS}
              >
                <option value="">Any size</option>
                {MIN_PARTICIPANT_OPTIONS.map((count) => (
                  <option key={count} value={count}>
                    {count} or more people
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between gap-4">
              <label htmlFor={fieldId("email-notes")} className="min-w-0 cursor-pointer">
                <span className="text-sm font-semibold text-gray-700 block">Email the notes</span>
                <span className="text-xs text-gray-500 block mt-0.5">
                  For meetings {agent.name} joins from {hasOthers ? "this" : "the"} calendar.
                </span>
              </label>
              <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
                <Switch
                  id={fieldId("email-notes")}
                  checked={connection.email_notes}
                  disabled={updateMutation.isPending}
                  onCheckedChange={(emailNotes) => handleUpdate({ emailNotes })}
                />
              </div>
            </div>
          </div>

          <div>
            <span className={FIELD_LABEL}>Coming up</span>
            {eventsLoading ? (
              <div className="flex items-center gap-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
                <span className="text-sm text-gray-600">Reading the calendar...</span>
              </div>
            ) : upcoming?.reconnect ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="text-sm text-gray-600 flex-1">
                  {agent.name} can't read this calendar any more. Connect it again to see what's coming up.
                </p>
                <button
                  type="button"
                  onClick={() => onConnect(connection.provider, connection.id)}
                  disabled={!!connectingProvider}
                  className="shrink-0 min-h-[44px] px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  style={softButtonStyle}
                >
                  {connectingProvider === connection.provider && <Loader2 className="h-4 w-4 animate-spin" />}
                  Reconnect
                </button>
              </div>
            ) : events.length === 0 ? (
              <p className="text-sm text-gray-600">
                No online meetings on the calendar in the next two weeks. New ones show up here as soon as they're
                added.
              </p>
            ) : (
              <ul className="space-y-2">
                {events.map((event) => {
                  const meeting = event.meeting;
                  const going = !!meeting;
                  const canSwitch = !meeting || isCancellableMeeting(meeting);
                  const isToggling = togglingEventId === event.id;
                  const note = elsewhereNote(event);

                  return (
                    <li key={event.id} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {event.title || PROVIDER_LABELS[event.meeting_provider]}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {[
                              eventTimeLine(event),
                              PROVIDER_LABELS[event.meeting_provider],
                              event.participants > 1 ? `${event.participants} people` : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                        {canSwitch ? (
                          <div className="flex min-h-11 min-w-11 shrink-0 items-center justify-center">
                            {isToggling ? (
                              <Loader2 className="h-4 w-4 animate-spin" style={{ color: brand }} />
                            ) : (
                              <Switch
                                checked={going}
                                onCheckedChange={(next) => handleToggle(event, next)}
                                aria-label={`${agent.name} joins ${event.title ?? "this meeting"}`}
                              />
                            )}
                          </div>
                        ) : (
                          <span
                            className="shrink-0 px-2 py-1 rounded-full text-xs font-medium bg-white shadow-sm border"
                            style={{ color: brand, borderColor: `${brand}55` }}
                          >
                            {STATE_LABELS[meeting!.state]}
                          </span>
                        )}
                      </div>
                      {note && <p className="text-xs text-gray-500 mt-2">{note}</p>}
                      {event.overlaps && (
                        <p className="text-xs mt-2" style={{ color: brand }}>
                          Overlaps with another meeting {agent.name} is joining. It will join both.
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};
